import {
  FeaturedUpsellMode,
  Hustler,
  HustlerConfig,
  Trainer,
  Workout,
  WorkoutDuration,
  WorkoutEquipment,
  WorkoutEquipmentPiece,
} from '../dao/core-dao'
import { coreService } from '../service/core-service'
import { logger } from '../util/logger'
import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { EventBusActionType, EventBusEntityType, eventBusUtil } from 'src/util/event-bus-util'
import { WORKOUTS_QUERY_KEY } from 'src/util/query-keys'

/** Assembled client-side at the point the paywall is triggered. No endpoint backs it. */
export type PaywallContext = {
  /** The workout the visitor could not play, when the paywall came from a play attempt. */
  blockedWorkoutId?: string
  /** Which free workouts this account has already completed. */
  playedFeaturedWorkoutIds: string[]
  /** Whole days since the account was created, or `undefined` if that is unknown. */
  daysSinceSignUp?: number
  /** Where the paywall was opened from, so upsell conversion is separable from blocked-play. */
  source: 'blocked-play' | 'featured-upsell'
}

export type SessionData = {
  me?: Hustler
  hustlerConfig: HustlerConfig
  /**
   * True until the first `getConfig()` attempt settles, success or failure.
   *
   * `hustlerConfig` starts on a fail-closed default, so without this a consumer cannot tell
   * "this account has no catalog access" from "we have not asked yet" — and anything that
   * HIDES a surface on the first reading gets it wrong for the whole of that window.
   */
  isHustlerConfigLoading: boolean
  setMe: React.Dispatch<React.SetStateAction<Hustler | undefined>>
  refreshMyData: () => Promise<void>
  hasActiveSubscription: boolean
  toggleSubscriptionModal: (context?: PaywallContext) => void
  showSubscriptionModal: boolean
  /**
   * Why the paywall was opened, captured at the trigger. Read by the paywall surface and by
   * analytics, so a conversion can be attributed to the thing that prompted it rather than
   * inferred from whatever screen happened to be mounted.
   */
  paywallContext?: PaywallContext
  trainers?: Trainer[]
  getFirstTrainerFromWorkout: (item: Workout) => Trainer | undefined
  trainerById: (id: string) => Trainer | undefined
  renderDuration: (id: string) => string | undefined
  renderEquipmentPieces: (workoutEquipment: WorkoutEquipment[]) => string
  completedWorkoutIds: Set<string>
  completeWorkout: (w: Workout) => void
}

export const SessionContext = createContext<SessionData>({} as SessionData)

export const useSessionContext: () => SessionData = () => {
  const context = useContext(SessionContext)
  if (!context) throw new Error('SessionContext must be used within SessionContextProvider')
  return context
}

/**
 * Runs one `refreshMyData` step behind its own error boundary, so one endpoint going down
 * cannot stop the rest from loading.
 *
 * Module scope on purpose: it closes over nothing from the component — only the imported
 * `logger` — so defining it inside `refreshMyData` just allocated a fresh closure on every
 * call for no benefit.
 */
const load = async (label: string, apply: () => Promise<void>): Promise<void> => {
  try {
    await apply()
  } catch (err) {
    logger.error(`refreshMyData: ${label} failed`, err)
  }
}

export const SessionContextProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
  const [me, setMe] = useState<Hustler>()
  const [trainers, setTrainers] = useState<Trainer[]>()
  const [durations, setDurations] = useState<WorkoutDuration[]>()
  const [equipment, setEquipment] = useState<WorkoutEquipmentPiece[]>()
  const [hustlerConfig, setHustlerConfig] = useState<HustlerConfig>({
    showWorkoutDates: true,
    showNewMonthlyWorkouts: false,
    newWorkoutsTitle: `This Week's Programme`,
    // Default to NO access until the real config arrives. The opposite default would flash
    // the catalog open for a moment on every load, which is the one direction this gate
    // must not fail in on the client.
    hasFullCatalogAccess: false,
    featuredUpsell: { mode: FeaturedUpsellMode.EVERY, firstN: 3, cooldownDays: 7 },
  })
  const [isHustlerConfigLoading, setIsHustlerConfigLoading] = useState(true)
  const queryClient = useQueryClient()
  const [completedWorkoutIds, setCompletedWorkoutIds] = useState(new Set<string>())
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [paywallContext, setPaywallContext] = useState<PaywallContext>()

  const equipmentPiecesLabels = useMemo(() => {
    return (equipment ?? []).reduce((map, piece) => {
      map.set(piece.id, piece.label ?? '')
      return map
    }, new Map<string, string>())
  }, [equipment])
  const trainerById = useCallback(
    (trainerId: string): Trainer | undefined => {
      if (!trainers) return undefined
      return trainers.filter((trainer: Trainer) => trainerId === trainer.id)[0]
    },
    [trainers],
  )

  const getFirstTrainerFromWorkout = useCallback(
    (item: Workout): Trainer | undefined => {
      if ((item.trainers ?? []).length === 0) return undefined
      return (item.trainers[0].id && trainerById(item.trainers[0].id)) || undefined
    },
    [trainerById],
  )

  const renderDuration = useCallback(
    (durationId: string): string | undefined => {
      if (!durations) return undefined
      const duration = durations.filter((duration: WorkoutDuration) => durationId === duration.id)[0]
      return `${duration.durationValue} ${duration.durationUnit.slice(0, 3)}`
    },
    [durations],
  )

  const renderEquipmentPieces = (workoutEquipment: WorkoutEquipment[]): string => {
    if (!equipment) return 'No equipment required'
    return (
      workoutEquipment
        .filter((eq) => !!eq.equipmentPieceId)
        .map((eq) => equipmentPiecesLabels.get(eq.equipmentPieceId))
        .join(', ') || 'No equipment required'
    )
  }

  const toggleSubscriptionModal = useCallback((context?: PaywallContext): void => {
    // Only set on open; closing leaves the last context in place rather than racing the
    // modal's exit animation to clear it.
    if (context) setPaywallContext(context)
    setShowSubscriptionModal((modalShown) => !modalShown)
  }, [])

  /**
   * Each fetch applies its own result and its own failure. One endpoint going down must
   * not stop the others from loading.
   *
   * This was seven bare sequential awaits under the single `.catch` at the call site,
   * with `getConfig()` fifth — so a rejection from trainers, durations or subscriptions
   * skipped `setHustlerConfig` entirely and left `hasFullCatalogAccess` sitting at its
   * default of `false`. Since GH-867 that default paywalls every surface, so an unrelated
   * endpoint could revoke a paying subscriber's access until they reloaded the page.
   *
   * The two defaults point in opposite directions, and both are deliberate: the server's
   * predicate fails OPEN (`catalog-access-service.ts`), because a false negative there
   * locks a subscriber out of the catalog, while this one starts closed to avoid flashing
   * the catalog open before the config lands. What was wrong was letting an unrelated
   * failure strand the client on that closed default. Mobile already guards the same
   * ground with `retry: 2` in `use-hustler-config.ts`. (GH-904)
   *
   * Still sequential, deliberately. `refresh-token-interceptor` de-duplicates nothing, so
   * running these concurrently would turn one expired access token into seven parallel
   * refresh calls.
   */
  const refreshMyData = async (): Promise<void> => {
    await load('getMe', async () => setMe(await coreService.getMe()))
    await load('getMySubscriptions', async () => setHasActiveSubscription(!!(await coreService.getMySubscriptions())))
    await load('getTrainers', async () => setTrainers(await coreService.getTrainers()))
    await load('listWorkoutDurations', async () => setDurations(await coreService.listWorkoutDurations()))
    await load('getConfig', async () => setHustlerConfig(await coreService.getConfig()))
    // Settled either way. A failed config attempt is still an answer for visibility purposes:
    // consumers then fall back to the predicate itself, which is the same thing the app did
    // before the field existed. What must not happen is staying "loading" forever and
    // permanently hiding a surface.
    setIsHustlerConfigLoading(false)
    await load('getWorkoutEquipmentPieces', async () => setEquipment(await coreService.getWorkoutEquipmentPieces()))
    await load('getCompletedWorkouts', async () => {
      const completedWorkouts = await coreService.getCompletedWorkouts()
      setCompletedWorkoutIds(
        completedWorkouts.reduce((s, cw) => {
          s.add(cw.workout.id)
          return s
        }, new Set<string>()),
      )
    })
  }

  useEffect(() => {
    refreshMyData().catch((err) => logger.error(err))
  }, [])

  /**
   * Subscribing mid-session has to take effect without a reload.
   *
   * Since GH-866 the server withholds `videoUrl` from accounts without full catalog access, so
   * a freshly-subscribed account is holding a page full of workouts it now *can* play but has
   * no URLs for — and a featured shelf it should no longer be shown. Both are cached query
   * results, so both need dropping.
   *
   * `SUBSCRIPTION.CREATE` is emitted when Stripe redirects back from a completed checkout
   * (`use-checkout-return.ts`) — the only browser-side signal that a subscription now exists.
   */
  useEffect(() => {
    const subscription = eventBusUtil().subscribe(
      { entities: [EventBusEntityType.SUBSCRIPTION], actions: [EventBusActionType.CREATE] },
      async () => {
        await refreshMyData()
        await queryClient.invalidateQueries([WORKOUTS_QUERY_KEY])
      },
    )
    return () => subscription.unsubscribe()
  }, [queryClient])

  const completeWorkout = (w: Workout): void => {
    setCompletedWorkoutIds((prev) => {
      const st = new Set(prev)
      st.add(w.id)
      return st
    })
  }
  return (
    <SessionContext.Provider
      value={{
        me,
        hustlerConfig,
        isHustlerConfigLoading,
        setMe,
        refreshMyData,
        hasActiveSubscription,
        trainers,
        getFirstTrainerFromWorkout,
        trainerById,
        toggleSubscriptionModal,
        showSubscriptionModal,
        paywallContext,
        renderDuration,
        renderEquipmentPieces,
        completedWorkoutIds,
        completeWorkout,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}
